import { WebMcpToolResult } from './webmcp.types';

// Same validation rules as the create-user form so agent input is held to the UI's standard.
export const COUNTRY_CODE_PATTERN = /^\+[0-9]{1,4}$/;
export const PHONE_PATTERN = /^[0-9]{10}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function textResult(payload: unknown): WebMcpToolResult {
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
  return { content: [{ type: 'text', text }] };
}

export function errorResult(message: string): WebMcpToolResult {
  return { content: [{ type: 'text', text: `Error: ${message}` }], isError: true };
}

export function asTrimmedString(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

/** Returns the names of required fields that are missing or blank. */
export function findMissingFields(input: Record<string, unknown>, required: string[]): string[] {
  return required.filter((field) => !asTrimmedString(input?.[field]));
}

/**
 * Finds the option whose code or display name matches the value (case-insensitive).
 * Agents usually know names ("North Service Center") rather than codes, so both are accepted.
 */
export function matchOption<T>(
  options: T[],
  value: string,
  getCode: (option: T) => unknown,
  getName: (option: T) => unknown,
): T | undefined {
  const wanted = asTrimmedString(value).toLowerCase();
  if (!wanted) {
    return undefined;
  }
  return (
    options.find((option) => asTrimmedString(getCode(option)).toLowerCase() === wanted) ??
    options.find((option) => asTrimmedString(getName(option)).toLowerCase() === wanted)
  );
}

/** Lists options as "Name (code)" so the agent can retry with a valid value. */
export function describeOptions<T>(
  options: T[],
  getCode: (option: T) => unknown,
  getName: (option: T) => unknown,
): string {
  if (!options.length) {
    return 'none available';
  }
  return options.map((option) => `${asTrimmedString(getName(option))} (${asTrimmedString(getCode(option))})`).join(', ');
}

/** Pulls the backend's ApiResponse message out of an HttpErrorResponse, falling back to the generic message. */
export function extractErrorMessage(error: any): string {
  return asTrimmedString(error?.error?.message) || asTrimmedString(error?.message) || 'Request failed.';
}
